import { Button, ButtonGroup, ComponentTrackingType, Flex, Icon, Text } from '@homebotapp/hb-react-component-catalog'
import { ReactNode } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { toggleAuthModal } from '../../../actions/homeSearchAuthModal'
import { setCreateClientFrom } from '../../../actions/navigation'
import { GlobalTrackingEventLocation } from '../../../constants/tracking'
import history from '../../../history'
import { buyerLinksMappedToAmplitude, FROM } from '../../../mappings/navigation'
import { useTrackingContext } from '../../../providers/tracking'
import { selectClientHomes, selectClientId, selectClientIsGuest } from '../../../store/selectors/client'
import {
  selectHomebotListingsFeature,
  selectIsSoloInsuranceAgent,
  selectIsSoloRea
} from '../../../store/selectors/customerProfile'
import { selectHomeId } from '../../../store/selectors/home'
import { selectActiveItem, selectClientShouldSeeMarketsAndBuyerNavButtons } from '../../../store/selectors/navigation'
import {
  routeToHome,
  selectBuyingReportRoute,
  selectMarketExploreRoute,
  selectRouteToSearch
} from '../../../store/selectors/routes'
import { selectHideBottomNav, setClickedListItem, setListItemLoading } from '../../../store/slices/nav'
import { NavigationComponents } from '../../../types/navigation'
import { hasCustomerToken } from '../../../auth'

const MSG = defineMessages({
  home: {
    id: 'BottomNav.own',
    defaultMessage: 'Own'
  },
  buy: {
    id: 'BottomNav.buy',
    defaultMessage: 'Buy'
  },
  markets: {
    id: 'BottomNav.markets',
    defaultMessage: 'Markets'
  },
  search: {
    id: 'BottomNav.search',
    defaultMessage: 'Search'
  }
})

export const BottomNav = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const activeDropdownItem = useSelector(selectActiveItem)
  const isSoloRea = useSelector(selectIsSoloRea)
  const isSoloInsuranceAgent = useSelector(selectIsSoloInsuranceAgent)
  const isClientOfUnsponsoredAgent = isSoloRea && isSoloInsuranceAgent
  const isGuest = useSelector(selectClientIsGuest)
  const showMarketsAndBuyerNavButtons = useSelector(selectClientShouldSeeMarketsAndBuyerNavButtons)
  const searchEnabled = useSelector(selectHomebotListingsFeature)
  const clientId = useSelector(selectClientId)
  const buyingReportTo = useSelector(selectBuyingReportRoute)
  const marketsTo = useSelector(selectMarketExploreRoute)
  const searchTo = useSelector(selectRouteToSearch)
  const hideBottomNav = useSelector(selectHideBottomNav)
  const homeId = useSelector(selectHomeId)
  const homes = useSelector(selectClientHomes)
  // need to fall back to homes[0].id if user refreshes (or starts out) on search route, since no home will be set in "state.home"
  // this logic happens in apps/clients-frontend/src/routes/Report/Reports.jsx meaning it only runs on report routes
  const currentSelectedHomeId = homeId ?? homes?.[0]?.id

  interface NavButtonProps {
    onClick: () => void
    children: ReactNode & Omit<ReactNode, 'undefined' | 'null'>
    'aria-selected': boolean
    tracking: ComponentTrackingType
  }

  const NavButton = ({ onClick, 'aria-selected': ariaSelected, tracking, children }: NavButtonProps) => (
    <Button
      onClick={onClick}
      aria-selected={ariaSelected}
      variant='ghost'
      height='100%'
      borderRadius='0'
      borderTopWidth='2px'
      borderTopStyle='solid'
      borderTopColor='var(--brand-primary-border-100)'
      flexDirection='column'
      flex={1}
      margin={0}
      marginInlineStart='0 !important'
      _selected={{
        color: 'brand.700',
        borderTopStyle: 'solid',
        borderColor: 'brand.700 !important'
      }}
      tracking={tracking}
    >
      {children}
    </Button>
  )

  const NavText = ({ children }: { children: string }) => (
    <Text size='2xs' marginBottom={0}>
      {children}
    </Text>
  )

  const isSearchButtonSelected = () => {
    // note - this might be able to be simplified to remove fav listings and saved searches once the existing mobile nav is updated
    return (
      activeDropdownItem === NavigationComponents.SEARCH_HOMES ||
      activeDropdownItem === NavigationComponents.FAVORITE_LISTINGS ||
      activeDropdownItem === NavigationComponents.SAVED_SEARCHES ||
      activeDropdownItem === NavigationComponents.LISTING
    )
  }

  const isOwnButtonSelected = () => {
    return activeDropdownItem === currentSelectedHomeId || activeDropdownItem === NavigationComponents.ADD_A_HOME
  }

  const handleOnClick = (key: NavigationComponents) => {
    // Keep legacy tracking working for now
    if (key !== NavigationComponents.OWNER_DASHBOARD) {
      trackingClient?.buyerNavigationLinkClicked({
        buyer_navigation_link: buyerLinksMappedToAmplitude[key]
      })
    }

    switch (key) {
      case NavigationComponents.OWNER_DASHBOARD:
        if (isGuest && !hasCustomerToken()) {
          dispatch(toggleAuthModal(true))
          return
        }
        if (homes.length === 0 || !homes) {
          // push user to add home route
          history.push(routeToHome(clientId, 'new'))
        }
        if (currentSelectedHomeId) {
          dispatch(setClickedListItem(currentSelectedHomeId))
          dispatch(setListItemLoading(true))
          history.push(routeToHome(clientId, currentSelectedHomeId))
          return
        }
        break
      case NavigationComponents.BUYING_REPORT:
        if (isGuest && !hasCustomerToken()) {
          dispatch(toggleAuthModal(true))
          return
        }
        dispatch(setClickedListItem(key))
        history.push(buyingReportTo)
        break
      case NavigationComponents.EXPLORE_MARKETS:
        if (isGuest && !hasCustomerToken()) {
          dispatch(toggleAuthModal(true))
          return
        }
        dispatch(setClickedListItem(key))
        dispatch(setCreateClientFrom(FROM.MARKET_TAB))
        history.push(marketsTo)
        break
      case NavigationComponents.SEARCH_HOMES:
        dispatch(setClickedListItem(key))
        history.push(searchTo)
        return
      default:
        break
    }
  }

  if (hideBottomNav || isClientOfUnsponsoredAgent) {
    return null
  }

  return (
    <Flex
      css={{
        position: '-webkit-sticky',
        bottom: '0'
      }}
      zIndex='modal'
      position='fixed'
      height='54px'
      width='100%'
      bottom='0'
      // account for when built-in bottom nav disappears on mobile
      paddingBottom={`calc(1rem + env(safe-area-inset-bottom))`}
    >
      <ButtonGroup
        flexBasis='100%'
        justifyContent='space-around'
        bg='card-bg-layer-1'
        zIndex={8}
        position='fixed'
        height='54px'
        width='100%'
        bottom='0'
      >
        <NavButton
          onClick={() => {
            handleOnClick(NavigationComponents.OWNER_DASHBOARD)
          }}
          aria-selected={isOwnButtonSelected()}
          tracking={{
            guid: 'uPJt1bJYz6sjaS9MAF8B5n',
            ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_BOTTOM_NAV,
            descriptor: MSG.home.defaultMessage
          }}
        >
          <Icon name={isOwnButtonSelected() ? 'home' : 'house'} marginBottom={1} />
          <NavText>{intl.formatMessage(MSG.home)}</NavText>
        </NavButton>
        {/* todo could combine these two conditionals */}
        {(isGuest || showMarketsAndBuyerNavButtons) && (
          <NavButton
            onClick={() => {
              handleOnClick(NavigationComponents.BUYING_REPORT)
            }}
            aria-selected={activeDropdownItem === NavigationComponents.BUYING_REPORT}
            tracking={{
              guid: '5vXfHMheZYV3eLPH5EXWm8',
              ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_BOTTOM_NAV,
              descriptor: MSG.buy.defaultMessage
            }}
          >
            <Icon
              name={activeDropdownItem === NavigationComponents.BUYING_REPORT ? 'buy-filled' : 'dollar-circle'}
              marginBottom={1}
            />
            <NavText>{intl.formatMessage(MSG.buy)}</NavText>
          </NavButton>
        )}
        {(isGuest || showMarketsAndBuyerNavButtons) && (
          <NavButton
            onClick={() => {
              handleOnClick(NavigationComponents.EXPLORE_MARKETS)
            }}
            aria-selected={activeDropdownItem === NavigationComponents.EXPLORE_MARKETS}
            tracking={{
              guid: '5MngyASRep9wmkUAgL9xZA',
              ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_BOTTOM_NAV,
              descriptor: MSG.markets.defaultMessage
            }}
          >
            <Icon name='map-marked-alt' marginBottom={1} />
            <NavText>{intl.formatMessage(MSG.markets)}</NavText>
          </NavButton>
        )}
        {searchEnabled && (
          <NavButton
            onClick={() => {
              handleOnClick(NavigationComponents.SEARCH_HOMES)
            }}
            aria-selected={isSearchButtonSelected()}
            tracking={{
              guid: 'guuCyEVyAP8mUbfcxRjxFM',
              ui_context: GlobalTrackingEventLocation.GLOBAL_MOBILE_BOTTOM_NAV,
              descriptor: MSG.search.defaultMessage
            }}
          >
            <Icon name={isSearchButtonSelected() ? 'search2-filled' : 'search2'} marginBottom={1} />
            <NavText>{intl.formatMessage(MSG.search)}</NavText>
          </NavButton>
        )}
      </ButtonGroup>
    </Flex>
  )
}
